package horcrux.frontend.common.views.auth

import horcrux.common.api.dtos.types.EmailAddress
import horcrux.common.utils.safelet.safeLet
import horcrux.frontend.common.BooleanState
import horcrux.frontend.common.GlobalAppState
import horcrux.frontend.common.MaybeState
import horcrux.frontend.common.State
import horcrux.frontend.common.value
import io.ktor.util.logging.KtorSimpleLogger
import mui.material.AlertColor
import mui.material.Box
import mui.material.Button
import mui.material.ButtonVariant
import mui.material.FormControlMargin
import mui.material.TextField
import mui.system.sx
import react.FC
import react.Props
import react.ReactNode
import react.dom.html.ReactHTML.form
import react.dom.onChange
import web.cssom.px
import web.html.ButtonType
import web.html.InputType

private val logger = KtorSimpleLogger("horcrux.frontend.auth.SignUp")

external interface SignUpProps : Props {
    var globalAppState: GlobalAppState
    var emailAddressMaybeState: MaybeState<EmailAddress>
    var authEnumState: State<AuthEnum>
}

val SignUp = FC<SignUpProps> { props ->
    val passwordMaybeState = MaybeState<String>()
    var confirmPasswordMaybeState = MaybeState<String?>()
    val passwordsMatchState = BooleanState(true)

    val emailAddressMaybe = props.emailAddressMaybeState.get()
    val passwordMaybe = passwordMaybeState.get()
    val confirmPasswordMaybe = confirmPasswordMaybeState.get()
    val passwordsMatch = passwordsMatchState.get()

    Box {
        sx {
            this.marginTop = 1.px
        }
        this.component = form
        this.onSubmit = {
            it.preventDefault()
            safeLet(emailAddressMaybe, passwordMaybe) { email, password ->
                if (passwordsMatch) {
                    props.globalAppState.submitAuthenticationClientAPIRequest { authenticationClient ->
                        authenticationClient
                            .signUp(email, password)
                            .onLeft { cause ->
                                logger.error("There was an error creating your account", cause)
                                props.globalAppState.updateBanner(
                                    AlertColor.error,
                                    "There was an error creating your account:\n${cause.message}"
                                )
                            }.onRight {
                                props.authEnumState.set(AuthEnum.SIGN_IN)
                                props.globalAppState.updateBanner(
                                    AlertColor.success,
                                    "Your account was created! Please check your inbox and verify your email."
                                )
                            }.bind()
                    }
                }
            }
        }
        TextField {
            this.margin = FormControlMargin.normal
            this.required = true
            this.fullWidth = true
            this.id = "email"
            this.label = ReactNode("Email Address")
            this.type = InputType.email
            this.name = "email"
            this.autoComplete = "email"
            this.autoFocus = true
            this.value = props.emailAddressMaybeState.get()?.value
            this.onChange = {
                props.emailAddressMaybeState.set(EmailAddress(it.value()))
            }
        }
        TextField {
            this.margin = FormControlMargin.normal
            this.required = true
            this.fullWidth = true
            this.id = "password"
            this.label = ReactNode("Password")
            this.type = InputType.password
            this.name = "password"
            this.defaultValue = passwordMaybe
            this.autoComplete = "new-password"
            this.onChange = {
                val newPassword = it.value()
                passwordMaybeState.set(newPassword)
                passwordsMatchState.set(newPassword == confirmPasswordMaybe || (confirmPasswordMaybe == null))
            }
        }
        TextField {
            this.margin = FormControlMargin.normal
            this.required = true
            this.fullWidth = true
            this.id = "confirm-password"
            this.label = ReactNode("Confirm Password")
            this.type = InputType.password
            this.name = "confirm-password"
            this.defaultValue = confirmPasswordMaybe
            this.error = !passwordsMatch
            this.helperText = if (passwordsMatch) null else ReactNode("Passwords must match")
            this.autoComplete = "new-password"
            this.onChange = {
                val newConfirmPassword = it.value()
                confirmPasswordMaybeState.set(newConfirmPassword)
                passwordsMatchState.set(passwordMaybe == newConfirmPassword)
            }
        }
        Button {
            this.type = ButtonType.submit
            this.formMethod = "POST"
            this.fullWidth = true
            this.variant = ButtonVariant.contained
            sx {
                this.marginTop = 3.px
                this.marginBottom = 2.px
            }
            +"Create Account"
        }
    }
}
